import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid, Header } from 'semantic-ui-react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { pathOr, omit } from 'ramda';

import { ClassProp } from 'helpers/types';
import { format } from 'date-fns';
import FormSelectList from 'components/FormComponents/FormSelect';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import SelectAudience, {
  AudienceSelectEvent,
  AttachedAudiences,
} from '../SelectAudience';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { useGetMarketplaces } from 'helpers/useGetMarketplaces';
import {
  PromotionTypes,
  getMarketingFee,
  filterTargetingRules,
  buildFinancialTerms,
  handleError,
  handleSuccess,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  buildTimeStamp,
  checkDates,
  dateIsAfterCurrentDate,
  DATE_FORMAT,
  getCurrentCSTDateWithTime,
  midnight,
  oneMinuteToMidnight,
  cstToUTC,
} from 'helpers/date';
import {
  Segment,
  PromotionType,
  PulseSegmentResponse,
  PulseFormData,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import {
  CREATE_TEMPORARY_OFFER_SEGMENT,
  GET_CAMPAIGN_BY_UUID,
  GET_OFFER,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

import styles from './PulseSegment.styles';

type Props = {
  classes: ClassProp<ReturnType<typeof styles>>;
  segment: Segment;
  offerUuid: string;
  merchantId: string;
  cancelUpdate: () => void;
  attachedAudiences: AttachedAudiences;
};

const shouldShowBaseCashback = (promoType: PromotionType | unknown) =>
  promoType === PromotionType.CASH_FLASH;

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Offer Pulsed Successfully.',
  },
];

const PulseSegment: FunctionComponent<Props> = ({
  segment,
  offerUuid,
  merchantId,
  cancelUpdate,
  classes,
  attachedAudiences,
}) => {
  const [insertionOrder, setInsertionOrder] = useState<string>('');
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [showBaseCashback, setShowBaseCashback] = useState<boolean>(
    shouldShowBaseCashback(segment.promotionType),
  );
  const [userName, setUserName] = useState<string | null>(null);

  const currentAudienceUuid = pathOr(
    null,
    ['audience', 'audienceUuid'],
    segment,
  );
  const financialTermItems = pathOr(
    '',
    ['financialTerm', 'financialTermItems'],
    segment,
  );
  const campaignUuid = pathOr('', [0, 'campaignId'], financialTermItems);
  const marketingFee = getMarketingFee(financialTermItems) as string;
  const {
    MarketplaceOptions,
    buildMarketplaces,
    loading: loadingMarketplaces,
  } = useGetMarketplaces();

  const methods = useForm<PulseFormData>({
    mode: 'onBlur',
    defaultValues: {
      marketingFee,
      audience: currentAudienceUuid,
      marketplaces: segment.marketplaces,
      segmentType: segment.segmentType,
      cashbackAmount:
        segment.cashbackAmount === 0 ? null : segment.cashbackAmount,
      cashbackPercentage:
        segment.cashbackPercentage === 0 ? null : segment.cashbackPercentage,
      cashbackDailyLimit: segment.cashbackDailyLimit,
      baseCashbackAmount: segment.cashbackAmount,
      baseCashbackPercentage: segment.cashbackPercentage,
      pulseStartDt: new Date(segment.startDate),
      pulseEndDt: segment.endDate && new Date(segment.endDate),
      pulseStartTm: format(midnight(), 'HH:mm'),
      pulseEndTm: format(oneMinuteToMidnight(), 'HH:mm'),
    },
  });

  const {
    register,
    formState,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    watch,
    trigger,
  } = methods;
  const { touched } = formState;

  const CampaignResponse = useQuery(GET_CAMPAIGN_BY_UUID, {
    variables: { params: { campaignUuid } },
  });
  const { data, loading } = CampaignResponse;

  const handlePulseSegmentResponse = (response: PulseSegmentResponse) => {
    handleSuccess(defaultSuccessMessage);
    cancelUpdate();
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    CREATE_TEMPORARY_OFFER_SEGMENT,
    {
      onCompleted: handlePulseSegmentResponse,
      onError: handleError,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const {
    data: { userProfile },
    loading: profileLoading,
  } = useQuery<any>(GET_PROFILE);

  useEffect(() => {
    register({ name: 'segmentType' });
    register({ name: 'promotionType' });
    register({ name: 'marketplaces' });
    register({ name: 'audience' });
    register(
      { name: 'pulseStartDt' },
      {
        required: 'Start Date is required',
        validate: {
          oneOf: () =>
            getValues().pulseEndDt === null ||
            checkDates(getValues().pulseStartDt, getValues().pulseEndDt) ||
            'Start Date must come before End Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(
              getCurrentCSTDateWithTime(value, getValues().pulseStartTm),
            ) || "Start Date can't be earlier than today",
        },
      },
    );
    register(
      { name: 'pulseEndDt' },
      {
        required: 'End Date is required',
        validate: {
          oneOf: () =>
            checkDates(getValues().pulseStartDt, getValues().pulseEndDt) ||
            'End Date must come after Start Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(value) ||
            "End Date can't be earlier than today",
        },
      },
    );
    register({ name: 'pulseStartTm' }, { required: 'Start Time is required' });
    register({ name: 'pulseEndTm' }, { required: 'End Time is required' });
  }, [register]);

  const endDate = watch('pulseEndDt');

  useEffect(() => {
    trigger('pulseStartDt');
  }, [endDate]);

  useEffect(() => {
    if (loading) return;

    const ticket = pathOr(
      '',
      ['campaignByUuid', 'insertionOrder', 'ticket'],
      data,
    );
    setInsertionOrder(ticket);
  }, [data, loading]);

  useEffect(() => {
    if (profileLoading) return;
    const { givenName, familyName } = userProfile;
    setUserName(`${givenName} ${familyName}`);
  }, [userProfile, profileLoading]);

  useEffect(() => {
    if (audienceError) {
      setError('audience', {
        message: audienceError,
      });
    } else {
      clearErrors('audience');
    }
  }, [audienceError]);

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) => cancelUpdate();

  const changePromoType = (promoType: any) => {
    setShowBaseCashback(shouldShowBaseCashback(promoType));
  };

  const handleAudienceChange = ({
    audienceId,
    audienceDates = [],
  }: AudienceSelectEvent) => {
    if (!audienceId || audienceId === currentAudienceUuid) {
      setAudienceError(null);
      return;
    }

    const startDate = getValues().pulseStartDt;
    const endDate = getValues().pulseEndDt;
    if (!startDate || !endDate) {
      setAudienceError('start date and end date must be set first');
      return;
    }

    let error = null;

    audienceDates.forEach((dateRange) => {
      const segmentStartDate = new Date(dateRange[0]);
      const segmentEndDate = new Date(dateRange[1]);
      if (startDate < segmentEndDate && endDate > segmentStartDate) {
        error = 'Audience is already used during these dates';
      }
    });

    setAudienceError(error);
  };

  const onSubmit = ({
    pulseStartDt,
    pulseStartTm,
    pulseEndDt,
    pulseEndTm,
    cashbackAmount,
    cashbackPercentage,
    baseCashbackAmount,
    baseCashbackPercentage,
    cashbackDailyLimit,
    marketingFee,
    promotionType,
    marketplaces,
    audience,
    ...data
  }: PulseFormData) => {
    if (audienceError) {
      setError('audience', {
        message: audienceError,
      });
      return;
    }
    const { offerTitleOverride, hideOfferForSegment } = segment;
    const targetingRules = filterTargetingRules({ segment, audience });
    const newFinancialTermItems = buildFinancialTerms(
      financialTermItems,
      parseFloat(cashbackPercentage as string),
      parseFloat(cashbackAmount as string),
      parseFloat(marketingFee),
    );

    const startTimeStamp = buildTimeStamp(
      format(pulseStartDt, DATE_FORMAT),
      pulseStartTm,
    );
    const endTimeStamp =
      pulseEndDt && buildTimeStamp(format(pulseEndDt, DATE_FORMAT), pulseEndTm);

    const pulsedSegment = {
      ...data,
      offerTitleOverride,
      hideOfferForSegment,
      targetingRules,
      promotionType,
      financialTerm: {
        financialTermItems: newFinancialTermItems,
      },
      marketplaces: buildMarketplaces(marketplaces),
      segmentPriority: 100,
      startDate: cstToUTC(startTimeStamp),
      endDate: endTimeStamp && cstToUTC(endTimeStamp),
      cashbackPercentage: parseFloat(cashbackPercentage as string),
      cashbackAmount: parseFloat(cashbackAmount as string),
      baseCashbackPercentage: parseFloat(baseCashbackPercentage as string),
      baseCashbackAmount: parseFloat(baseCashbackAmount as string),
      cashbackDailyLimit: parseFloat(cashbackDailyLimit as string),
      overrideCashbackCheck: true,
      updatedBy: userName,
    };
    const omitOneOf =
      cashbackPercentage && cashbackPercentage !== ''
        ? 'cashbackAmount'
        : 'cashbackPercentage';
    const omitOneOfBaseValue =
      baseCashbackPercentage !== '0'
        ? 'baseCashbackAmount'
        : 'baseCashbackPercentage';

    const tempSegment = omit([omitOneOf, omitOneOfBaseValue], pulsedSegment);

    runMutation({
      variables: {
        params: { ...tempSegment },
      },
    });
  };

  const checkForOneOf = () => {
    if (!!touched.cashbackAmount && !!touched.cashbackPercentage) {
      const cbAmount = getValues().cashbackAmount;
      const cbPercent = getValues().cashbackPercentage;
      return !!cbAmount && !!cbPercent ? false : true;
    }
    return true;
  };

  return (
    <Container className={classes.container}>
      <Header as="h2">Pulse Segment</Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="hidden"
            value={offerUuid}
            name="offerUuid"
            ref={register}
          />
          <input
            type="hidden"
            value={segment.uuid}
            name="segmentUuid"
            ref={register}
          />
          <input
            type="hidden"
            value={insertionOrder}
            name="insertionOrderRef"
            ref={register}
          />
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Promotion type"
                  name="promotionType"
                  dropDownOptions={PromotionTypes}
                  defaultValue={segment.promotionType}
                  onSelectChange={changePromoType}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <FormSelectList
                  label="Marketplaces"
                  name="marketplaces"
                  dropDownOptions={MarketplaceOptions}
                  defaultValue={segment.marketplaces}
                  multiple
                  loading={loadingMarketplaces}
                  wrapSelection
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormDateInput
                  name="pulseStartDt"
                  label="Start Date"
                  placeholder={new Date(segment.startDate)}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="pulseStartTm"
                  useCST={false}
                  label="Start Time (CST)"
                  placeholder={midnight()}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormDateInput
                  name="pulseEndDt"
                  label="End Date"
                  placeholder={new Date(segment.startDate)}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="pulseEndTm"
                  useCST={false}
                  label="End Time (CST)"
                  placeholder={oneMinuteToMidnight()}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormInput
                  name="cashbackAmount"
                  type="number"
                  placeholder="Enter Cashback Amount"
                  label="Cashback Amount"
                  register={register({
                    validate: {
                      oneOf: () =>
                        checkForOneOf() ||
                        'Can only have one Cashback Amount or Percent',
                    },
                  })}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="cashbackPercentage"
                  type="number"
                  placeholder="Enter Cashback %"
                  label="Cashback %"
                  register={register({
                    validate: {
                      oneOf: () =>
                        checkForOneOf() ||
                        'Can only have one Cashback Amount or Percent',
                    },
                  })}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="cashbackDailyLimit"
                  type="number"
                  placeholder="Enter a Daily Cashback Limit"
                  label="Daily Cashback Limit"
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="marketingFee"
                  type="number"
                  placeholder="Enter Marketing Fee for Campaign"
                  label="Marketing Fee"
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>
            {showBaseCashback && (
              <Grid.Row>
                <Grid.Column width={4}>
                  <label>
                    <span className={classes.fieldLabel}>
                      Base Cashback Amount
                    </span>
                    <p>${segment.cashbackAmount}</p>
                    <input
                      type="hidden"
                      name="baseCashbackAmount"
                      ref={register}
                    />
                  </label>
                </Grid.Column>
                <Grid.Column width={4}>
                  <label>
                    <span className={classes.fieldLabel}>
                      Base Cashback Percentage
                    </span>
                    <p>{segment.cashbackPercentage}%</p>
                    <input
                      type="hidden"
                      name="baseCashbackPercentage"
                      ref={register}
                    />
                  </label>
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row className={classes.formRow}>
              <Grid.Column width={8}>
                <SelectAudience
                  merchantId={merchantId}
                  onSelectChange={handleAudienceChange}
                  attachedAudiences={attachedAudiences}
                  currentAudienceUuid={currentAudienceUuid}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || loadingMarketplaces}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Pulse Offer'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(PulseSegment);
